import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import { useUser } from '@/store/user';
import { useGlobalStore } from '@/store/global-store-base';
import type { ArticleDtoSeasonality, ProductDtoSeasonality, SeasonDto, SkuDtoSeasonality } from '@/ApiClient.ts';
import { whereNotNull } from '@lib/type-helpers.ts';
import { runWithBomDataLossConfirm } from '@lib/bom-data-loss';
import { computed } from 'vue';
import { groupBySection } from '@lib/group-by-section';

export function splitArchived<T extends { isArchived: boolean }>(seasons: T[]): { active: T[]; archived: T[] } {
  return { active: seasons.filter((s) => !s.isArchived), archived: seasons.filter((s) => s.isArchived) };
}

export const ARCHIVED_SECTION = 'Archived';

/** The section a season is listed under in a dropdown that sets archived seasons apart. */
export const archivedSection = (season: { isArchived: boolean } | undefined) =>
  season?.isArchived ? ARCHIVED_SECTION : undefined;

/** Orders items for a menu with an "Archived" heading: the rest first, then the archived ones under it. */
export const groupArchived = <T>(items: readonly T[], isArchived: (item: T) => boolean) =>
  groupBySection(items, (item) => (isArchived(item) ? ARCHIVED_SECTION : undefined));

/** No season (`null`/`undefined`) is never archived. */
export const isArchivedSeasonId = (seasonId: number | null | undefined) =>
  seasonId != null && !!useSeasons().byId[seasonId]?.isArchived;

/** Whether a per-season column is shown while archived seasons may be hidden. */
export const isSeasonColumnShown = (seasonId: number | null, showArchived: boolean) =>
  showArchived || !isArchivedSeasonId(seasonId);

export const useSeasons = defineStore('seasons', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.season_GetSeasons,
    dto: 'SeasonDto',
    create: api.season_AddSeason,
    update: api.season_UpdateSeason,
    usage: api.season_CountUsages,
    signalR: 'SeasonUpdate',
  });

  const { loaded } = globalStoreBase;

  // Custom delete (not via globalStoreBase.delete) so it can route through the
  // BOM-data-loss confirm flow and thread `suppressBomDropWarning` to the backend.
  // Returns true if the season was deleted, false if the user cancelled the warning.
  const deleteSeason = async (id: number): Promise<boolean> => {
    const brandId = useUser().brandId;
    const result = await runWithBomDataLossConfirm((suppressBomDropWarning) =>
      api.season_DeleteSeason(brandId, id, suppressBomDropWarning)
    );
    if (result === null) return false;
    loaded.value = loaded.value.filter((s) => s.id !== id);
    return true;
  };

  const active = computed(() => loaded.value.filter((s) => !s.isArchived));
  const archived = computed(() => loaded.value.filter((s) => s.isArchived));

  // Override getByIds to respect loaded order instead of IDs order. Seasons are always ordered the same way.
  const getByIds = (ids: number[]): SeasonDto[] => {
    const idSet = new Set(ids);
    return loaded.value.filter((season) => idSet.has(season.id));
  };

  const getFromSeasonalities = (seasonalities: { id?: number | undefined }[]): SeasonDto[] =>
    getByIds(seasonalities.map((s) => s.id).filter(whereNotNull));

  const getLatestSeasonFromIds = (seasonIds: number[]): SeasonDto | undefined => {
    // Seasons are already ordered by backend, just get them and take the first
    const seasons = getByIds(seasonIds);
    return seasons.length > 0 ? seasons[0] : undefined;
  };

  return {
    ...globalStoreBase,
    delete: deleteSeason,
    getByIds,
    getFromSeasonalities,
    getLatestSeasonFromIds,
    loaded,
    active,
    archived,
  };
});
